/* eslint max-classes-per-file: ["error", 2] */
// services
import makeXhrRequest from './xhr';
// helper
import { createHeaders, handleResponse, initializeRequest, isAbortError } from './apiHelper';
// constants
import { CONTENT_TYPE, HTTP, HTTP_METHOD } from './const';
import type { ResourceData, ResultResourceData } from 'framework/crud';
import { recordHttpRequestDiagnostic } from 'framework/utils';
import type {
  APIRequestProps,
  ApiRequestOptions,
  ApiServices,
  ArtifactsAPIRequestProps,
  BaseRequestProps,
  RawRequestProps,
  RequestProps,
  RequestResponse,
} from './api.types';

const getFailedResponseText = async (response: Response): Promise<string | undefined> => {
  if (response.status < 400) {
    return undefined;
  }

  try {
    return await response.clone().text();
  } catch (error) {
    return undefined;
  }
};
class RequestError extends Error {
  status?: number | string;

  constructor(status: string | number = '', message: string = 'Something went wrong') {
    super();

    this.message = message;
    this.status = status;
    this.name = 'RequestError';
  }
}

export class ResourceNotFoundError extends RequestError {
  constructor(errorMessage?: string) {
    super(HTTP.NOT_FOUND, errorMessage);
  }
}

/**
 * Method for making API requests (any), should handle authorization rejection
 * @param method -- GET, POST, PUT, etc.
 * @param url -- request url, relative to backend base
 * @param data -- data to be sent
 * @param contentType -- Content-Type header value
 * @param eventTarget -- event
 * @param headers -- custom request headers, if needed
 * @returns {Promise}
 */

const makeRawRequest = ({
  url,
  method = HTTP_METHOD.GET,
  data,
  contentType = CONTENT_TYPE.JSON,
  eventTarget,
  headers = [],
  signal,
}: RawRequestProps): RequestResponse => {
  const startedAt = Date.now();
  const startedAtPerf = typeof window !== 'undefined' && window.performance ? window.performance.now() : null;
  const requestHeaders = createHeaders(headers, contentType);
  const params = {
    method,
    data,
    contentType,
    eventTarget,
  };
  const init = initializeRequest(requestHeaders, params);

  if (signal) {
    init.signal = signal;
  }

  return fetch(url, init).then(async response => {
    const finishedAtPerf = typeof window !== 'undefined' && window.performance ? window.performance.now() : null;
    recordHttpRequestDiagnostic({
      method,
      path: url,
      status: response.status,
      durationMs: startedAtPerf !== null && finishedAtPerf !== null ? Math.round(finishedAtPerf - startedAtPerf) : null,
      time: new Date(startedAt).toISOString(),
      responseText: await getFailedResponseText(response),
    });
    return handleResponse(response);
  });
};

/**
 *
 * Method for making API requests, should handle authorization rejection
 * @param baseUrl - base url
 * @param method -- GET, POST, PUT, etc.
 * @param url -- request url, relative to backend base
 * @param data -- data to be sent
 * @param contentType -- Content-Type header value
 * @param eventTarget -- event
 * @param headers -- custom request headers, if needed
 * @param authProvider -- authentication provider component
 * @returns {Promise}
 */

const makeAPIRequest = async ({
  authProvider,
  unauthenticatedRequestHandler,
  baseUrl,
  url,
  method = HTTP_METHOD.GET,
  data,
  contentType,
  eventTarget,
  headers = [],
  handleError,
  timeout,
  signal,
}: APIRequestProps): Promise<ResultResourceData> => {
  const request = async () => {
    const controller = timeout != null ? new AbortController() : undefined;
    // Keep caller cancellation connected while the service reads the response body, too.
    const requestSignal =
      controller && signal ? AbortSignal.any([signal, controller.signal]) : (controller?.signal ?? signal);
    let timedOut = false;
    const timeoutId = controller
      ? setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, timeout)
      : undefined;

    try {
      requestSignal?.throwIfAborted();
      const requestHeaders = [...headers];
      if (authProvider?.getAuthHeader) {
        requestHeaders.push(await authProvider.getAuthHeader());
      }
      // Authentication may outlive the collection which requested this page.
      requestSignal?.throwIfAborted();
      return await makeRawRequest({
        url: `${baseUrl}${url}`,
        method,
        data,
        contentType,
        eventTarget,
        headers: requestHeaders,
        signal: requestSignal,
      });
    } catch (error) {
      if (!signal?.aborted && timedOut && isAbortError(error)) {
        throw { status: 408, message: `Request timeout after ${timeout}ms`, originalError: error };
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  try {
    return await request();
  } catch (error) {
    // Caller cancellation is not an HTTP failure and must not trigger a toast or authentication retry.
    if (signal?.aborted || isAbortError(error)) throw error;

    if (
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      error.status === HTTP.UNAUTHORIZED &&
      unauthenticatedRequestHandler
    ) {
      return unauthenticatedRequestHandler.handleUnauthenticated(error, handleError, request);
    }
    return handleError(error as Response);
  }
};
const buildSendRequest =
  ({ baseUrl, authProvider, unauthenticatedRequestHandler, method, handleError }: RequestProps) =>
  (url: string, data?: ResourceData, options?: ApiRequestOptions) => {
    return makeAPIRequest({
      authProvider,
      unauthenticatedRequestHandler,
      baseUrl,
      url,
      method,
      data,
      eventTarget: null,
      headers: [],
      handleError,
      ...options,
    });
  };

const buildSendGetRequest =
  ({ baseUrl, authProvider, unauthenticatedRequestHandler, handleError }: BaseRequestProps) =>
  (url: string, options?: ApiRequestOptions) =>
    buildSendRequest({
      baseUrl,
      authProvider,
      unauthenticatedRequestHandler,
      method: HTTP_METHOD.GET,
      handleError,
    })(url, undefined, options);

const makeUploadFileRequest = async ({
  authProvider,
  unauthenticatedRequestHandler,
  baseUrl,
  url = '/artifacts',
  method = HTTP_METHOD.POST,
  data,
  eventTarget,
  onProgress,
  headers = [],
  handleError,
}: ArtifactsAPIRequestProps): Promise<ResultResourceData> => {
  const requestUrl = `${baseUrl}${url}`;
  const startedAt = Date.now();
  const startedAtPerf = typeof window !== 'undefined' && window.performance ? window.performance.now() : null;
  const contentType = CONTENT_TYPE.MULTIPART;

  if (authProvider?.getAuthHeader) {
    headers.push(await authProvider.getAuthHeader());
  }

  const requestHeaders = createHeaders(headers, contentType);

  const params = {
    method,
    data,
    contentType,
    eventTarget,
  };
  const init = initializeRequest(requestHeaders, params);

  return makeXhrRequest({
    url: requestUrl,
    opts: init,
    onProgress,
  })
    .then(response => {
      const finishedAtPerf = typeof window !== 'undefined' && window.performance ? window.performance.now() : null;
      recordHttpRequestDiagnostic({
        method,
        path: requestUrl,
        status: response.status,
        durationMs:
          startedAtPerf !== null && finishedAtPerf !== null ? Math.round(finishedAtPerf - startedAtPerf) : null,
        time: new Date(startedAt).toISOString(),
        responseText: response.status >= 400 ? response.responseText : undefined,
      });
      return handleResponse(response as unknown as Response);
    })
    .catch(async error => {
      if (error.status === HTTP.UNAUTHORIZED && unauthenticatedRequestHandler) {
        return unauthenticatedRequestHandler.handleUnauthenticated(error, handleError, () =>
          makeUploadFileRequest({
            authProvider,
            unauthenticatedRequestHandler,
            baseUrl,
            url,
            onProgress,
            method,
            data,
            contentType,
            eventTarget,
            handleError,
          }),
        );
      }

      return handleError(error);
    });
};

const buildSendUploadFile =
  ({ baseUrl, authProvider, unauthenticatedRequestHandler, handleError }: BaseRequestProps) =>
  (requestData: Partial<ArtifactsAPIRequestProps>): RequestResponse =>
    makeUploadFileRequest({
      ...requestData,
      baseUrl,
      authProvider,
      unauthenticatedRequestHandler,
      handleError,
    } as any);

const apiService = (baseUrl: string, services: ApiServices) => {
  const { authProvider, errorHandler, unauthenticatedRequestHandler } = services;
  const { handleError } = errorHandler;

  return {
    baseUrl,
    sendGetRequest: buildSendGetRequest({ baseUrl, authProvider, unauthenticatedRequestHandler, handleError }),
    sendPostRequest: buildSendRequest({
      baseUrl,
      authProvider,
      unauthenticatedRequestHandler,
      method: HTTP_METHOD.POST,
      handleError,
    }),
    sendPutRequest: buildSendRequest({
      baseUrl,
      authProvider,
      unauthenticatedRequestHandler,
      method: HTTP_METHOD.PUT,
      handleError,
    }),
    sendPatchRequest: buildSendRequest({
      baseUrl,
      authProvider,
      unauthenticatedRequestHandler,
      method: HTTP_METHOD.PATCH,
      handleError,
    }),
    sendDeleteRequest: buildSendRequest({
      baseUrl,
      authProvider,
      unauthenticatedRequestHandler,
      method: HTTP_METHOD.DELETE,
      handleError,
    }),
    uploadFile: buildSendUploadFile({
      baseUrl,
      authProvider,
      unauthenticatedRequestHandler,
      handleError,
    }),
  };
};

export default apiService;
